import React, { useCallback, useContext, useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import UserAvatar from '@totem/components/common/avatar/UserAvatar';
import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import Tooltip from '@totem/components/common/tooltip/Tooltip';
import UserContext from '@totem/components/orgManagement/orgDetails/users/applied/userContext';
import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { User } from '@totem/components/users/container/types';
import {
  getRoleFilter,
  getRoleString,
} from '@totem/components/users/container/utilities';
import { ORGANIZATION_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  iconButton: {
    marginLeft: '0.4rem',
  },
};

const XOrgUsersTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    input,
    setInput,
    userData,
    loading,
    totalRecords,
    onAction,
    organization,
  } = useContext(UserContext);
  const [isSending, setIsSending] = useState<boolean>(false);

  const sendRemoveOrganizationFromUser = useCallback(
    async (organizationId: string, userId: string) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      fetch(`${ORGANIZATION_ENDPOINT}/${organizationId}/users/${userId}`, {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        onAction('refresh', null);
      });
    },
    [],
  );

  const handleRemoveUser = (selectedUser: User) => {
    sendRemoveOrganizationFromUser(organization.id, selectedUser.id);
  };

  const columns: ColumnProps<User>[] = [
    {
      title: '',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 5,
      render: (text, record) => {
        return (
          <div className="center-table-cell">
            <UserAvatar size="large" user={record} />
          </div>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => sortStringAscending(compA.email, compB.email),
    },
    {
      title: 'First',
      dataIndex: 'firstName',
      key: 'firstName',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.firstName, compB.firstName),
    },
    {
      title: 'Last',
      dataIndex: 'lastName',
      key: 'lastName',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.lastName, compB.lastName),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (text: string, user: User) => {
        const roleString = getRoleString(user.organization.role);
        return <div>{roleString}</div>;
      },
      filters: getRoleFilter(),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginDate',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '-'),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.lastLoginDate, compB.lastLoginDate),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: User) => (
        <>
          {permissions.internalSystemAdministratorIBOrg.hasPermissions(
            userProfile,
          ) &&
            record.organization.isActive && (
              <IBTooltip
                content={permissions.internalSystemAdministratorIBOrg.tooltip}
              >
                <Tooltip title="Remove user from organization" placement="top">
                  <Button
                    type="primary"
                    shape="circle"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveUser(record)}
                    style={styles.iconButton}
                  />
                </Tooltip>
              </IBTooltip>
            )}
        </>
      ),
    },
  ];

  const getUsers = () => {
    if (userData !== null) {
      if (userData.users !== null) {
        return userData.users;
      }
    }
    return [];
  };

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<User>,
    sorter,
  ) => {
    const { ...params } = filters;

    let sortDir: string = sorter.order === 'descend' ? '-1' : '1';
    if (typeof sorter.order === 'undefined' || sorter.order === null) {
      if (
        typeof input.sortDirection !== 'undefined' &&
        input.sortDirection !== null
      ) {
        sortDir = input.sortDirection;
      }
    }

    let sortField = input.sortField ? input.sortField : 'lastOccurrence';
    if (
      typeof sorter.field !== 'undefined' &&
      typeof sorter.order !== 'undefined'
    ) {
      sortField = sorter.field;
    }

    // @ts-ignore
    setInput({
      ...input,
      ...params,
      pageSize: updatedPagination.pageSize,
      page: updatedPagination.current,
      sortField,
      sortDirection: sortDir,
    });
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getUsers()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.page,
        pageSize: input.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey="id"
    />
  );
};

export default XOrgUsersTable;
